import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { monospaceFontFamily } from "src/providers/theme";
import { deleteApiKeys, recreateApiKeys } from "src/ui/containers/account/api";
import { copyToClipboard } from "src/ui/utils/clipboard";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { DangerOutlinedButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useAccountApiKeys } from "../../hooks/useAccountApiKeys";

const useStyles = makeStyles()((theme: Theme) => ({
    apiKeyContainer: {
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    apiKeyValue: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
        wordBreak: "break-all",
        fontFamily: monospaceFontFamily,
    },
    apiKeyActions: {
        display: "flex",
        gap: theme.spacing(1),
        marginLeft: "auto",
    },
}));

const maskApiKey = (apiKey: string) => `••••••••••••${apiKey.slice(-4)}`;

type ConfirmAction = "renew" | "delete";

function AccountApiKeyPage() {
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [isKeyRevealed, setIsKeyRevealed] = React.useState(false);
    const [confirmAction, setConfirmAction] = React.useState<ConfirmAction | null>(null);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const { apiKeys, setApiKeys, isLoading } = useAccountApiKeys();

    const handleApiKeysGeneration = async () => {
        setIsGenerating(true);

        try {
            const { model, message } = await recreateApiKeys();
            setApiKeys(model);
            showSuccessToast(message);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleApiKeysRenew = async () => {
        const { model, message } = await recreateApiKeys();
        setApiKeys(model);
        setIsKeyRevealed(false);
        showSuccessToast(message);
    };
    const handleApiKeysDeletion = async () => {
        const { message } = await deleteApiKeys();
        setApiKeys("");
        setIsKeyRevealed(false);
        showSuccessToast(message);
    };

    return (
        <PageContainer fullWidth={false} isLoading={isLoading}>
            <SectionPanel title={t("pages:settingsPage.apiKey.title")}>
                {apiKeys ? <div>{t("pages:settingsPage.apiKey.description")}</div> : null}
                <Box className={classes.apiKeyContainer}>
                    {apiKeys ? (
                        <>
                            <span className={classes.apiKeyValue}>{isKeyRevealed ? apiKeys : maskApiKey(apiKeys)}</span>
                            <div className={classes.apiKeyActions}>
                                <SecondaryButton
                                    startIcon={<i className={isKeyRevealed ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} />}
                                    name="toggle-api-key-visibility"
                                    onClick={() => setIsKeyRevealed((revealed) => !revealed)}
                                >
                                    {isKeyRevealed
                                        ? t("pages:settingsPage.apiKey.hideKey")
                                        : t("pages:settingsPage.apiKey.showKey")}
                                </SecondaryButton>
                                <SecondaryButton
                                    startIcon={<i className="fa-solid fa-copy" />}
                                    name="copy-api-key"
                                    onClick={() => void copyToClipboard(apiKeys)}
                                >
                                    {t("common:button.copy")}
                                </SecondaryButton>
                                <SecondaryButton
                                    startIcon={<i className="fa-solid fa-arrow-rotate-left" />}
                                    name="renew-api-key"
                                    onClick={() => setConfirmAction("renew")}
                                >
                                    {t("common:button.renew")}
                                </SecondaryButton>
                                <DangerOutlinedButton
                                    startIcon={<i className="fa-solid fa-fw fa-trash" />}
                                    name="delete-api-key"
                                    onClick={() => setConfirmAction("delete")}
                                >
                                    {t("common:button.delete")}
                                </DangerOutlinedButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <span>{t("pages:settingsPage.apiKey.noKey")}</span>
                            <div className={classes.apiKeyActions}>
                                <PrimaryOutlinedButton
                                    startIcon={<i className="fa-solid fa-key" />}
                                    onClick={() => void handleApiKeysGeneration()}
                                    loading={isGenerating}
                                    disabled={isGenerating}
                                    name="generate-api-key"
                                >
                                    {t("common:button.generate")}
                                </PrimaryOutlinedButton>
                            </div>
                        </>
                    )}
                </Box>
            </SectionPanel>
            <ConfirmDialog
                open={confirmAction === "renew"}
                onClose={() => setConfirmAction(null)}
                onConfirm={handleApiKeysRenew}
                title={t("pages:settingsPage.apiKey.renewDialog.title")}
                primaryActionText={t("pages:settingsPage.apiKey.renewDialog.confirm")}
            >
                <Typography variant="body1">{t("pages:settingsPage.apiKey.renewDialog.body")}</Typography>
            </ConfirmDialog>
            <ConfirmDialog
                open={confirmAction === "delete"}
                onClose={() => setConfirmAction(null)}
                onConfirm={handleApiKeysDeletion}
                title={t("pages:settingsPage.apiKey.deleteDialog.title")}
                primaryActionText={t("pages:settingsPage.apiKey.deleteDialog.confirm")}
                dangerButton
            >
                <Typography variant="body1">{t("pages:settingsPage.apiKey.deleteDialog.body")}</Typography>
            </ConfirmDialog>
        </PageContainer>
    );
}

export default withMetaDescription(AccountApiKeyPage, {
    title: i18next.t("pages:settingsPage.pages.apikey.metaTitle"),
});
